import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFoldoutMenu } from '../../../hooks/useFoldoutMenu/useFoldoutMenu';
import { Searchfield } from '../Text/Searchfield';
import {
  ChipButtonStyled,
  ClearButtonStyled,
  FloatingContainer,
  ItemsContainer,
  SearchWrapper,
  SpanStyled,
} from './styles';
import { MultiSelectItems } from './components/MultiSelectItems';
import { SingleSelectItems } from './components/SingleSelectItems';
import { ChipChevron } from './icons/ChipChevron';
import { ChipClear } from './icons/ChipClear';
import { type ChipButtonItem } from './types/types';

export type ChipButtonProps = {
  label: string;
  selectedValues: string[];
  treatAllAsNoSelection?: boolean;
  items: ChipButtonItem[];
  onChange?: (selectedValues: string[]) => void;
  testId?: string;
  /**
   * If items are more than this number, a search input will be shown
   */
  searchLimit?: number;
  /**
   * Enables single value selection behavior and hides checkboxes in the menu.
   */
  singleSelect?: boolean;
  /**
   * Action menus use full-height rows without a selected background.
   */
  menuVariant?: 'default' | 'actions';
  searchFieldPlaceholder?: string;
  children?: React.ReactNode;
  id?: string;
  allowSelectAll?: boolean;
  setOpenHandle?: (handle: (open: boolean) => void) => void;
  /**
   * When provided (singleSelect only), renders a delete icon next to each item in the menu.
   */
  onDeleteItem?: (value: string) => void;
  deleteItemAriaLabel?: (item: ChipButtonItem) => string;
};

export const ChipButton = ({
  label,
  selectedValues,
  onChange,
  testId,
  items,
  searchLimit = 10,
  singleSelect = false,
  menuVariant = 'default',
  searchFieldPlaceholder,
  treatAllAsNoSelection = false,
  children,
  id,
  allowSelectAll = false,
  setOpenHandle,
  onDeleteItem,
  deleteItemAriaLabel,
}: ChipButtonProps) => {
  const hasSelected = useMemo(() => {
    if (treatAllAsNoSelection) {
      return selectedValues.length > 0 && selectedValues.length !== items.length;
    }

    return selectedValues.length > 0;
  }, [selectedValues, items, treatAllAsNoSelection]);

  const [itemsToShow, setItemsToShow] = useState(items);

  useEffect(() => {
    setItemsToShow(items);
  }, [items]);

  const {
    refs,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    setIsOpen: setOpen,
    isOpen,
  } = useFoldoutMenu({ placement: 'bottom-start' });

  useEffect(() => {
    if (setOpenHandle) {
      setOpenHandle(setOpen);
    }
  }, [setOpen, setOpenHandle]);

  const labelToUse = useMemo(() => {
    if (!hasSelected) return label;

    if (selectedValues.length === 1) {
      const selectedItem = items.find((item) => item.value === selectedValues[0]);

      return selectedItem ? selectedItem.label : label;
    }

    return ` ${label} (${selectedValues.length})`;
  }, [hasSelected, items, label, selectedValues]);

  const filterItems = useCallback(
    (query: string) => {
      const filteredItems = items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      );

      setItemsToShow(filteredItems);
    },
    [items],
  );

  return (
    <>
      <ChipButtonStyled
        type="button"
        data-testid={testId || 'chip-button'}
        id={id}
        aria-pressed={hasSelected}
        active={isOpen}
        hasSelected={hasSelected}
        ref={(node) => {
          refs.setReference(node);
        }}
        {...getReferenceProps()}
        onClick={() => {
          setOpen(!isOpen);
        }}
      >
        <SpanStyled>{labelToUse}</SpanStyled>
        {hasSelected && (
          <ClearButtonStyled
            onClick={(e) => {
              e.stopPropagation();
              onChange?.([]);
            }}
          >
            <ChipClear />
          </ClearButtonStyled>
        )}
        {!hasSelected && <ChipChevron rotation={isOpen ? 180 : 0} />}
      </ChipButtonStyled>
      {isOpen && (
        <FloatingContainer
          variant={menuVariant}
          ref={(node) => {
            refs.setFloating(node);
          }}
          {...getFloatingProps()}
          style={floatingStyles}
        >
          {items.length > searchLimit && (
            <SearchWrapper>
              <Searchfield
                placeholder={searchFieldPlaceholder}
                onChange={(query) => {
                  filterItems(query);
                }}
              />
            </SearchWrapper>
          )}

          <ItemsContainer>
            {singleSelect ? (
              <SingleSelectItems
                actionMenu={menuVariant === 'actions'}
                items={itemsToShow}
                selectedValues={selectedValues}
                onChange={onChange}
                onSelect={() => {
                  setOpen(false);
                }}
                onDeleteItem={onDeleteItem}
                deleteItemAriaLabel={deleteItemAriaLabel}
              >
                {children}
              </SingleSelectItems>
            ) : (
              <MultiSelectItems
                items={itemsToShow}
                selectedValues={selectedValues}
                onChange={onChange}
                allowSelectAll={allowSelectAll}
              >
                {children}
              </MultiSelectItems>
            )}
          </ItemsContainer>
        </FloatingContainer>
      )}
    </>
  );
};
